"use client";

import { useEffect, useState } from "react";
import { FleetHubItem } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useFleetHubUpdates } from "@/hooks/use-fleethub-updates";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Search,
  Loader2,
  X,
  Package,
  Layers,
  Star,
  Crown,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import ProductCard from "@/components/products/product-card";
import { Button } from "@/components/ui/button";
import { DisplayProduct } from "@/types";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Filter types for FleetHub items
type ItemCategory = "product" | "kit" | "exclusive-product" | "exclusive-kit";

interface CategoryFilter {
  id: ItemCategory;
  label: string;
  icon: React.ReactNode;
  active: boolean;
}

interface FleetHubGridProps {
  items: FleetHubItem[];
  type: "user" | "company";
  companyId?: string | null;
  companyItems?: FleetHubItem[];
  onToggleUserFleetHubItem: (input: {
    itemId: string;
    itemType: "product";
  }) => Promise<{
    success: boolean;
    message: string;
    added?: boolean;
  }>;
  onAddToCompanyFleetHub: (input: {
    itemId: string;
    itemType: "product";
  }) => Promise<{
    success: boolean;
    message: string;
  }>;
  onRemoveFromCompanyFleetHub: (input: {
    itemId: string;
    itemType: "product";
  }) => Promise<{
    success: boolean;
    message: string;
  }>;
}

// Helper function to categorize FleetHub items
const categorizeItem = (item: FleetHubItem): ItemCategory => {
  if (item.isExclusive && item.hasAttachments) return "exclusive-kit";
  if (item.isExclusive && !item.hasAttachments) return "exclusive-product";
  if (!item.isExclusive && item.hasAttachments) return "kit";
  return "product";
};

// Get localStorage key for filters
const getFilterStorageKey = (type: "user" | "company") =>
  `fleethub-filters-${type}`;

export default function FleetHubGrid({
  items: initialItems,
  type,
  companyId,
  companyItems = [],
  onToggleUserFleetHubItem,
  onAddToCompanyFleetHub,
  onRemoveFromCompanyFleetHub,
}: FleetHubGridProps) {
  const [items, setItems] = useState<FleetHubItem[]>(initialItems);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const [itemToRemove, setItemToRemove] = useState<FleetHubItem | null>(null);

  // Initialize filters from localStorage
  const [filters, setFilters] = useState<CategoryFilter[]>(() => {
    const defaultFilters: CategoryFilter[] = [
      {
        id: "exclusive-kit",
        label: "Exclusive Kits",
        icon: <Crown className='h-4 w-4' />,
        active: true,
      },
      {
        id: "kit",
        label: "Kits",
        icon: <Layers className='h-4 w-4' />,
        active: true,
      },
      {
        id: "exclusive-product",
        label: "Exclusive Products",
        icon: <Star className='h-4 w-4' />,
        active: true,
      },
      {
        id: "product",
        label: "Products",
        icon: <Package className='h-4 w-4' />,
        active: true,
      },
    ];

    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(getFilterStorageKey(type));
      if (saved) {
        try {
          const savedFilters = JSON.parse(saved);
          return defaultFilters.map((filter) => ({
            ...filter,
            active: savedFilters[filter.id] ?? true,
          }));
        } catch {
          return defaultFilters;
        }
      }
    }
    return defaultFilters;
  });

  // Subscribe to FleetHub updates if this is a company FleetHub
  useFleetHubUpdates({
    companyId: type === "company" ? companyId || null : null,
    onUpdate: (update) => {
      // Refresh the items when we receive an update
      if (update.action === "add") {
        toast({
          title: "Item added to company FleetHub",
          description: "A new item has been added to the company FleetHub.",
        });
      } else if (update.action === "remove") {
        toast({
          title: "Item removed from company FleetHub",
          description: "An item has been removed from the company FleetHub.",
          variant: "destructive",
        });

        // Remove the item from the list if it exists
        setItems((current) =>
          current.filter(
            (item) =>
              item.itemId !== update.itemId || item.itemType !== update.itemType
          )
        );
      }
    },
  });

  // Update items when initialItems change
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  // Save filters to localStorage
  const saveFilters = (newFilters: CategoryFilter[]) => {
    if (typeof window !== "undefined") {
      const filterState = newFilters.reduce(
        (acc, filter) => {
          acc[filter.id] = filter.active;
          return acc;
        },
        {} as Record<string, boolean>
      );
      localStorage.setItem(
        getFilterStorageKey(type),
        JSON.stringify(filterState)
      );
    }
  };

  // Toggle filter
  const toggleFilter = (filterId: ItemCategory) => {
    const newFilters = filters.map((filter) =>
      filter.id === filterId ? { ...filter, active: !filter.active } : filter
    );
    setFilters(newFilters);
    saveFilters(newFilters);
  };

  // Filter items based on search and category filters
  const filteredItems = items.filter((item) => {
    // Search filter
    if (searchQuery) {
      const searchableText = [item.title, item.part_number, item.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!searchableText.includes(searchQuery.toLowerCase())) {
        return false;
      }
    }

    // Category filter
    const category = categorizeItem(item);
    const categoryFilter = filters.find((f) => f.id === category);
    return categoryFilter?.active ?? true;
  });

  const handleRemoveFromFleetHub = async (item: FleetHubItem) => {
    // Don't allow removal of exclusive products from company FleetHub
    if (type === "company" && item.isExclusive) {
      toast({
        title: "Cannot remove exclusive product",
        description:
          "This product is exclusive to your company and cannot be removed from the FleetHub.",
        variant: "destructive",
      });
      return;
    }

    setLoading((prev) => ({ ...prev, [item.id]: true }));

    try {
      if (type === "user") {
        const result = await onToggleUserFleetHubItem({
          itemId: item.itemId,
          itemType: item.itemType,
        });

        if (result.success) {
          // Remove the item from the list
          setItems(items.filter((i) => i.id !== item.id));
          toast({
            title: "Item removed",
            description: "Item removed from your FleetHub.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: result.message || "Failed to remove item.",
            variant: "destructive",
          });
        }
      } else if (type === "company") {
        const result = await onRemoveFromCompanyFleetHub({
          itemId: item.itemId,
          itemType: item.itemType,
        });

        if (result.success) {
          // Remove the item from the list
          setItems(items.filter((i) => i.id !== item.id));
          toast({
            title: "Item removed",
            description: "Item removed from company FleetHub.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: result.message || "Failed to remove item.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Error removing item from FleetHub:", error);
      toast({
        title: "Error",
        description: "Failed to remove item from FleetHub.",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleAddToCompanyFleetHub = async (item: FleetHubItem) => {
    setLoading((prev) => ({ ...prev, [item.id]: true }));

    try {
      const result = await onAddToCompanyFleetHub({
        itemId: item.itemId,
        itemType: item.itemType,
      });

      if (result.success) {
        toast({
          title: "Added to company FleetHub",
          description: "Item added to company FleetHub successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to add to company FleetHub.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding to company FleetHub:", error);
      toast({
        title: "Error",
        description: "Failed to add to company FleetHub.",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  // This is now handled above in the combined filteredItems

  return (
    <div className='space-y-4'>
      {/* Filter Badges */}
      <div className='flex flex-wrap items-center gap-1.5'>
        <span className='text-sm font-medium text-muted-foreground mr-1'>
          Filter:
        </span>
        {filters.map((filter) => (
          <Badge
            key={filter.id}
            variant={filter.active ? "default" : "outline"}
            className={`cursor-pointer transition-all duration-200 rounded-full px-3 py-1 text-xs ${
              filter.active
                ? "border-primary text-primary bg-primary/5 hover:bg-primary/10"
                : "border-border text-muted-foreground hover:bg-accent"
            }`}
            onClick={() => toggleFilter(filter.id)}
          >
            {filter.label}
          </Badge>
        ))}
      </div>

      {/* Search Input */}
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4' />
        <Input
          type='text'
          placeholder='Search items...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='pl-10 w-full h-9'
        />
      </div>

      {filteredItems.length > 0 ? (
        <GroupedItemsGrid
          items={filteredItems}
          type={type}
          companyId={companyId}
          companyItems={companyItems}
          loading={loading}
          onRemove={(item) => setItemToRemove(item)}
          onAddToCompany={handleAddToCompanyFleetHub}
        />
      ) : (
        <EmptyState searchQuery={searchQuery} type={type} />
      )}

      <AlertDialog
        open={!!itemToRemove}
        onOpenChange={(open) => !open && setItemToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {type === "company" && itemToRemove?.isExclusive
                ? "Cannot remove exclusive product"
                : "Remove item from FleetHub?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {type === "company" && itemToRemove?.isExclusive
                ? "This product is exclusive to your company and cannot be removed from the FleetHub."
                : `This will remove the item from your ${
                    type === "company" ? "company" : "personal"
                  } FleetHub. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {type === "company" && itemToRemove?.isExclusive
                ? "Close"
                : "Cancel"}
            </AlertDialogCancel>
            {!(type === "company" && itemToRemove?.isExclusive) && (
              <AlertDialogAction
                onClick={() => {
                  if (itemToRemove) {
                    handleRemoveFromFleetHub(itemToRemove);
                    setItemToRemove(null);
                  }
                }}
                className='bg-destructive hover:bg-destructive/90'
              >
                Remove
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Extracted components to improve readability

type EmptyStateProps = {
  searchQuery: string;
  type: "user" | "company";
  icon?: React.ReactNode;
};

function EmptyState({ searchQuery, type, icon }: EmptyStateProps) {
  return (
    <motion.div
      className='col-span-full text-center py-12'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {icon || (
        <FileText className='h-10 w-10 mx-auto text-muted-foreground mb-4' />
      )}
      <h2 className='text-2xl font-bold mb-4'>No Products Found</h2>
      <p className='text-muted-foreground'>
        {searchQuery
          ? `No products match your search "${searchQuery}"`
          : `No products in your ${type === "company" ? "company" : "personal"} FleetHub.`}
      </p>
    </motion.div>
  );
}

type ItemsGridProps = {
  items: FleetHubItem[];
  type: "user" | "company";
  companyId?: string | null;
  companyItems?: FleetHubItem[];
  loading: Record<string, boolean>;
  onRemove: (item: FleetHubItem) => void;
  onAddToCompany: (item: FleetHubItem) => Promise<void>;
};

// Grouped Items Grid component that groups by category without headers
function GroupedItemsGrid({
  items,
  type,
  companyId,
  companyItems = [],
  loading,
  onRemove,
  onAddToCompany,
}: ItemsGridProps) {
  // Group items by category and flatten them in the desired order
  const groupedItems = items.reduce(
    (acc, item) => {
      const category = categorizeItem(item);
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    },
    {} as Record<ItemCategory, FleetHubItem[]>
  );

  // Define the order of categories and flatten them without headers
  const orderedCategories: ItemCategory[] = [
    "exclusive-kit",
    "kit",
    "exclusive-product",
    "product",
  ];
  const flattenedItems = orderedCategories.flatMap(
    (category) => groupedItems[category] || []
  );

  return (
    <motion.div
      className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
      layout='position'
      transition={{
        duration: 0.4,
        type: "spring",
        stiffness: 250,
        damping: 35,
      }}
    >
      <AnimatePresence initial={false} mode='popLayout'>
        {flattenedItems.map((item, index) => {
          // Bottom: Add to Company button (if user type and companyId)
          let bottomButtons = undefined;
          if (type === "user" && companyId) {
            // Check if item exists in company FleetHub
            const isInCompanyFleetHub = companyItems.some(
              (companyItem) => companyItem.itemId === item.itemId
            );

            // Only show button if:
            // 1. Not in company FleetHub
            // 2. Not an exclusive product (exclusive products can't be manually added)
            if (!isInCompanyFleetHub && !item.isExclusive) {
              bottomButtons = (
                <div className='flex w-full'>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onAddToCompany(item);
                    }}
                    variant='outline'
                    className='gap-1 w-2/3'
                    disabled={loading[item.id]}
                  >
                    {loading[item.id] ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      <>+ Add to Company</>
                    )}
                  </Button>
                </div>
              );
            } else if (item.isExclusive) {
              // Show indicator for exclusive products
              bottomButtons = (
                <div className='flex w-full'>
                  <div className='flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md w-full justify-center'>
                    <span className='w-2 h-2 bg-primary rounded-full'></span>
                    Already in Company FleetHub
                  </div>
                </div>
              );
            }
          }

          // Overlay X button - don't show for exclusive products in company FleetHub
          const showRemoveButton = !(type === "company" && item.isExclusive);

          const removeButton = showRemoveButton ? (
            <Button
              variant='ghost'
              size='icon'
              className='absolute top-2 right-2 z-20 h-6 w-6 bg-background/80 backdrop-blur-sm shadow hover:bg-destructive hover:text-destructive-foreground transition-colors'
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRemove(item);
              }}
              disabled={loading[item.id]}
              aria-label='Remove from FleetHub'
            >
              {loading[item.id] ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                <X className='h-3 w-3' />
              )}
            </Button>
          ) : null;

          const mappedItem = item as unknown as DisplayProduct;

          // Determine the top tag based on item properties
          let topTags = item.hasAttachments ? "Kit" : "Product";
          if (item.isExclusive) {
            topTags = item.hasAttachments
              ? "Exclusive Kit"
              : "Exclusive Product";
          }

          return (
            <div key={item.id} className='relative w-full h-[450px]'>
              {removeButton}
              <ProductCard
                product={mappedItem}
                brandName={item.brand || ""}
                bottomButtons={bottomButtons}
                topTags={topTags}
                customizable={item.editor === "custom"}
                index={index}
                className='w-full h-full'
              />
            </div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );
}
